import React, { useMemo, useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { useNavigate, useLocation } from "react-router-dom";
import { DataGridPro, useGridApiRef, GRID_TREE_DATA_GROUPING_FIELD, gridRowGroupingNameSelector } from '@mui/x-data-grid-pro';
import { Box, Paper, Typography, Chip, LinearProgress } from '@mui/material';
import CommonButton from '../CommonButton/CommonButton';
import ListItemIcon from "@mui/material/ListItemIcon";
import DoneIcon from '@mui/icons-material/Done';
import { fetchCompaniesData, fetchCompaniesByRequestId, fetchActivityReportRequestById, mapActivityRequestStatusCodeToLabel } from '../../../services/activityReportService'
import { Snackbar, Alert } from "@mui/material";
import { useAuth } from '../../auth/AuthProvider';


const formatLastActivityFromIso = (iso) => {
    if (!iso) return null;
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return null;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
};

const normalizeGridStatusLabel = (raw) => {
    if (raw === null || raw === undefined || raw === '') return null;
    if (typeof raw === 'string') {
        const t = raw.trim();
        if (!t) return null;
        if (t.toLowerCase() === 'completed') return 'Complete';
        return t;
    }
    return String(raw);
};

const resolveCompanyStatus = (companyObj, requestStatusLabel, role = 'operations') => {
    const raw =
        companyObj?.company_status ??
        companyObj?.activity_status ??
        companyObj?.submission_status ??
        companyObj?.status_label ??
        (typeof companyObj?.status === 'string' ? companyObj.status : null);

    let label = normalizeGridStatusLabel(raw);

    if (!label && typeof companyObj?.status === 'number') {
        label = mapActivityRequestStatusCodeToLabel(companyObj.status, role);
    }

    return label || requestStatusLabel || '—';
};

const rowsUnderTreeGroupingKey = (groupingKey, allRows) => {
    if (groupingKey == null || groupingKey === '') return [];
    return allRows.filter(
        (r) => Array.isArray(r.path) && r.path.length > 0 && r.path[0] === groupingKey,
    );
};

const aggregateLabels = (rows, pick) => {
    const labels = [...new Set(rows.map(pick).filter(Boolean))];
    if (labels.length === 0) return '—';
    if (labels.length === 1) return labels[0];
    return labels.join(' · ');
};

const GradeReports = forwardRef(({
    searchText,
    tableRef,
    isCheckboxColumn,
    onCompaniesChange,
    isLocationColumn,
    isStatusColumn,
    isLastActivityColumn,
    isGradeColumn,
    isInvestmentCapitalColumn,
    isCashOutDateColumn,
    isActionColumn,
    onSelectedCompanies,
    selectedCompanies,
    isDoneAction,
    apiRef: apiRefProp,
    requestId,
}, ref) => {
    const {role} = useAuth();

    if (isStatusColumn === undefined) {
        isStatusColumn = true;
    };
    if (isLastActivityColumn === undefined) {
        isLastActivityColumn = true;
    }
    if (isGradeColumn === undefined) {
        isGradeColumn = false;
    }
    if (isCashOutDateColumn === undefined) {
        isCashOutDateColumn = false;
    }
    if (isActionColumn === undefined) {
        isActionColumn = true;
    }
    if (isDoneAction === undefined) {
        isDoneAction = true;
    }

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });
    const [selectedRows, setSelectedRows] = useState([]);
    const [gradeReports, setGradeReports] = useState([]);
    const [originalData, setOriginalData] = useState([]);
    const mockTreeData = [];
    const singleCompanyPartners = [];
    const partnerCompanyMap = {};

    // Use either onSelectedCompanies or selectedCompanies prop
    const excludedCompanies = selectedCompanies || onSelectedCompanies || [];

    // Create a ref to track if data has been fetched
    const hasFetched = React.useRef(false);

    const showSnackbar = (message, severity = "success") => {
        setSnackbar({ open: true, message, severity });
    };

    const handleCloseSnackbar = () => {
        setSnackbar((prev) => ({ ...prev, open: false }));
    };

    const generateStableId = (partnerName, companyName, index) => {
        // Handle undefined or null values
        const safePartnerName = partnerName || 'unknown';
        const safeCompanyName = companyName || 'unknown';

        // Create a unique string that will always be the same for the same combination
        const uniqueString = `${safePartnerName}|${safeCompanyName}`.toLowerCase().replace(/\s+/g, '_');


        return uniqueString;
    };

    // Helper function to filter out selected companies
    const filterOutSelectedCompanies = (data, selectedCompaniesList) => {
        if (!data || data.length === 0) return [];
        if (!selectedCompaniesList || selectedCompaniesList.length === 0) return data;

        // Create a Set of selected company IDs for O(1) lookup
        const selectedIds = new Set();
        selectedCompaniesList.forEach(company => {
            if (company && company.id) {
                selectedIds.add(company.id);
            }
        });

        // Filter out rows that have IDs in the selected set
        const filtered = data.filter(row => !selectedIds.has(row.id));

        console.log('Filtering companies:', {
            originalCount: data.length,
            selectedCount: selectedIds.size,
            filteredCount: filtered.length,
            selectedIds: Array.from(selectedIds)
        });

        return filtered;
    };

   const fetch2CompaniesData = async () => {
        setLoading(true);
        setError(null);
        try {
            if (requestId && typeof requestId === 'string') {
                const [companiesRes, requestRes] = await Promise.all([
                    fetchCompaniesByRequestId(requestId),
                    fetchActivityReportRequestById(requestId).catch(() => null),
                ]);
                const requestItem = requestRes?.data ?? null;
                const requestUpdatedAt = requestItem?.updated_at ?? null;
                const requestStatusCode =
                    typeof requestItem?.status === 'number' ? requestItem.status : null;
                const requestStatusLabel =
                    requestStatusCode != null
                        ? mapActivityRequestStatusCodeToLabel(requestStatusCode, role)
                        : null;
                processPartnersData(companiesRes.data, {
                    requestUpdatedAt,
                    requestStatusLabel,
                    role,
                });
            } else {
                const response = await fetchCompaniesData();
                processPartnersData(response.data, {});
            }
        } catch (error) {
            setError(error.message || "Failed to load portfolio data");
            showSnackbar("Failed to load portfolio data", "error");
        } finally {
            setLoading(false);
        }
    }

    const processPartnersData = (data, options = {}) => {
        const { requestUpdatedAt, requestStatusLabel, role: optionsRole } = options;
        const statusRole = optionsRole ?? role ?? 'operations';
        const lastActivityDisplay = formatLastActivityFromIso(requestUpdatedAt);

        console.log("processPartnersData - raw data: ", data);

        // Clear the arrays before processing new data
        mockTreeData.length = 0;
        // singleCompanyPartners.length = 0;

        // Clear the map
        Object.keys(partnerCompanyMap).forEach(key => delete partnerCompanyMap[key]);

        // Use a Set to track unique combinations
        const uniqueCombinations = new Set();
        const tempData = [];

        // Transform API data into tree structure for DataGridPro
        data.forEach((companyObj) => {

            const { company, grade, invested_capital, cashout, partners, deal_id } = companyObj;

            const rowLastActivityIso =
                requestUpdatedAt ||
                companyObj.updated_at ||
                companyObj.company_updated_at ||
                null;
            const rowStatus = resolveCompanyStatus(companyObj, requestStatusLabel, statusRole);


            if (partners && Array.isArray(partners)) {
                partners.forEach((partner, index) => {

                    // const id = generateStableId(partner?.partner_name, company);

                    // mockTreeData.push({
                    //     id: id,
                    //     path: [partner?.partner_name, company],
                    //     Partner: partner?.partner_name,
                    //     partnerDoc: partner?.partner_docname,
                    //     Company: company,
                    //     Status: "Complete",
                    //     LastActivity: null,
                    //     Collected: 10,
                    //     Grade: grade,
                    //     InvestmentCapital: Number(invested_capital?.replace(/[^0-9.]/g, "")) || 0,
                    //     CashOutDate: cashout,
                    // });

                    // Create a unique key for this combination
                    const combinationKey = `${partner?.partner_name}|${company}`;

                    // Skip if this combination already exists
                    if (uniqueCombinations.has(combinationKey)) {
                        console.warn(`Duplicate combination found: ${combinationKey}`);
                        return;
                    }

                    uniqueCombinations.add(combinationKey);

                    const id = generateStableId(partner?.partner_name, company, index);
                    const path = [partner?.partner_name, company];

                    tempData.push({
                        id: id,
                        path: path,
                        Partner: partner?.partner_name,
                        partnerDoc: partner?.partner_docname,
                        Company: company,
                        deal_id: deal_id || null,  // ✅ ADD THIS
                        Status: rowStatus,
                        LastActivity: rowLastActivityIso,
                        Collected: 10,
                        Grade: grade,
                        location: companyObj.location || "",
                        InvestmentCapital: typeof invested_capital === 'string'
                            ? Number(invested_capital.replace(/[^0-9.]/g, "")) || 0
                            : Number(invested_capital) || 0,
                        CashOutDate: cashout,
                    });

                });
            }
        });



        // Build a map of Partners to their unique Companies
        tempData.forEach(row => {
            if (!partnerCompanyMap[row.Partner]) {
                partnerCompanyMap[row.Partner] = new Set();
            }
            partnerCompanyMap[row.Partner].add(row.Company);
        });

        // Find Partners with only one unique Company
        Object.entries(partnerCompanyMap).forEach(([partner, companiesSet]) => {
            if (companiesSet.size === 1) {
                singleCompanyPartners.push(partner);
            }
        });

        //  singleCompanyPartners.forEach((partner) => {
        //     // Find all rows in mockTreeData with this Partner
        //     mockTreeData.forEach((row) => {
        //         if (row.Partner === partner && Array.isArray(row.path) && row.path.length > 1) {
        //             // Update path to only include the partner name
        //             row.path = [partner];
        //         }
        //     });
        // });

        // console.log("Processed Tree Data: ", mockTreeData);
        // setGradeReports(mockTreeData);

        // Update paths for partners with single companies
        const processedData = tempData.map(row => {
            if (singleCompanyPartners.includes(row.Partner) && Array.isArray(row.path) && row.path.length > 1) {
                return {
                    ...row,
                    path: [row.Partner] // Update path to only include partner name
                };
            }
            return row;
        });

        // Validate unique paths before setting state
        const pathMap = new Map();
        let hasDuplicates = false;

        processedData.forEach(row => {
            const pathKey = JSON.stringify(row.path);
            if (pathMap.has(pathKey)) {
                console.error(`Duplicate path found: ${pathKey} for rows:`, pathMap.get(pathKey), row.id);
                hasDuplicates = true;
            } else {
                pathMap.set(pathKey, row.id);
            }
        });

        if (hasDuplicates) {
            console.error('Duplicate paths detected! Please check your data.');
            showSnackbar("Duplicate tree paths detected in data", "error");
            return;
        }

        console.log("Processed Tree Data: ", processedData);
        mockTreeData.push(...processedData);

        setOriginalData(processedData);

        const filteredData = filterOutSelectedCompanies(processedData, excludedCompanies);
        setGradeReports(filteredData);

        // Mark that data has been fetched
        hasFetched.current = true;

    }

    if (onSelectedCompanies === undefined || onSelectedCompanies === null || onSelectedCompanies.length === 0) {
        onSelectedCompanies = () => { };
    } else {
        console.log("onSelectedCompanies: ", onSelectedCompanies);
        // removing from mockTreeData id rows based on onSelectedCompanies ids
        onSelectedCompanies.forEach(company => {
            const index = mockTreeData.findIndex(row => row.id === company.id);
            if (index !== -1) {
                mockTreeData.splice(index, 1);
            }
        })
    }

    useEffect(() => {
        fetch2CompaniesData();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [requestId]);

    // Effect to re-filter when excludedCompanies changes
    useEffect(() => {
        if (originalData.length > 0) {
            console.log('Excluded companies changed, re-filtering data...');
            const filteredData = filterOutSelectedCompanies(originalData, excludedCompanies);
            setGradeReports(filteredData);
        }
    }, [excludedCompanies, originalData]);

    const navigate = useNavigate();
    const location = useLocation();


    const internalApiRef = useGridApiRef();
    const apiRef = apiRefProp || internalApiRef;

    const flagButtonStyles = {
        padding: 2,
        paddingInline: 1,
        margin: 0,
        minWidth: "0px",
        position: "relative",
        left: "2px",
        borderRadius: "80%",
        height: "24px",
    };

    const gridStyles_1 = {
        height: '80vh', width: '100%', bgcolor: '#f5f5f5'
    }

    const gridStyles_2 = {
        height: '59vh', width: '100%', bgcolor: '#f5f5f5'
    }

    const selectedRowsData = [];

    // Function to compute aggregates for parent nodes
    const computeAggregates = (data) => {

        if (!data || data.length === 0) return [];

        // Create a copy of the data
        const processedData = [...data];

        // Create a map for quick lookup
        const dataMap = new Map();
        processedData.forEach(row => {
            dataMap.set(row.id, { ...row, children: [], parentId: null });
        });

        // Build parent-child relationships
        processedData.forEach(row => {
            if (row.path && row.path.length > 1) {
                // Find parent by matching path
                const parentPath = row.path.slice(0, -1);
                console.log('Finding parent for row:', row, 'Parent path:', parentPath);
                const parent = processedData.find(p =>
                    JSON.stringify(p.path) === JSON.stringify(parentPath)
                );
                if (parent && parent.id !== row.id) {
                    const parentData = dataMap.get(parent.id);
                    const childData = dataMap.get(row.id);
                    if (parentData && childData && !parentData.children.includes(childData)) {
                        parentData.children.push(childData);
                        childData.parentId = parent.id;
                    }
                }
            }
        });

        // Function to compute sum of a property for all descendants
        const sumDescendants = (node, property) => {
            let total = 0;
            const stack = [node];

            while (stack.length > 0) {
                const current = stack.pop();
                // Add leaf node values (nodes without children)
                if (current.children.length === 0) {
                    total += current[property] || 0;
                }
                // Add child nodes to stack
                stack.push(...current.children);
            }

            console.log(`Total ${property} for node ${node.id}:`, total);
            return total;
        };

        /**
         * Computes fractional progress as a decimal (0 to 1)
         * @param {Object} node - The root node to start computation from
         * @param {string} progressProperty - The property name that contains progress (0-100 or 0-1)
         * @returns {number} - Fractional progress from 0 to 1
         */
        const computeFractionalProgress = (node, progressProperty) => {
            let totalProgress = 0;
            let totalWeight = 0;
            const stack = [node];

            while (stack.length > 0) {
                const current = stack.pop();

                // Process leaf nodes only
                if (current.children.length === 0) {
                    const progress = current[progressProperty] || 0;
                    // Convert to 0-1 range if needed (assuming 0-100)
                    //const normalizedProgress = progress > 1 ? progress / 100 : progress;
                    totalProgress += progress;
                    totalWeight++;
                }

                // Add child nodes to stack
                stack.push(...current.children);
            }

            return totalWeight > 0 ? totalProgress + '/' + totalWeight * 10 : 0;
        };

        /**
         * Computes the latest LastActivity date from all leaf descendants
         * @param {Object} node - The root node to start computation from
         * @returns {string|null} - Latest date in YYYY-MM-DD format or null
         */
        const computeLatestActivity = (node) => {
            let latestDate = null;
            let latestDateObj = null;
            const stack = [node];

            while (stack.length > 0) {
                const current = stack.pop();

                // Process leaf nodes only
                if (current.children.length === 0) {
                    const activityDate = current.LastActivity;

                    // Only consider valid date strings (not 0 or falsy)
                    if (activityDate && activityDate !== 0 && typeof activityDate === 'string') {
                        try {
                            const dateObj = new Date(activityDate);
                            if (!isNaN(dateObj.getTime())) {
                                // Format as YYYY-MM-DD for comparison
                                const formattedDate = dateObj.toISOString().split('T')[0];

                                // Update latest date
                                if (!latestDate || formattedDate > latestDate) {
                                    latestDate = formattedDate;
                                    latestDateObj = dateObj;
                                }
                            }
                        } catch (e) {
                            console.warn('Invalid date format:', activityDate);
                        }
                    }
                }

                // Add child nodes to stack
                stack.push(...current.children);
            }



            if (!latestDateObj) return null;

            // Convert to Month-Day format
            const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

            return {
                isoDate: latestDate,
                monthDay: `${monthNames[latestDateObj.getMonth()]} ${latestDateObj.getDate()}`,
                dateObj: latestDateObj
            };
        };

        const countLeafDescendants = (node) => {
            let count = 0;
            const stack = [node];
            while (stack.length > 0) {
                const current = stack.pop();
                if (current.children.length === 0) {
                    count++;
                } else {
                    stack.push(...current.children);
                }
            }
            return count;
        };

        // Update parent nodes with aggregated sums
        processedData.forEach(row => {
            const node = dataMap.get(row.id);
            if (node && node.children && node.children.length > 0) {
                // This is a parent node
                const collectedSum = computeFractionalProgress(node, 'Collected');
                const capitalSum = sumDescendants(node, 'InvestmentCapital');
                const latestActivity = computeLatestActivity(node);

                // Count leaf descendants
                const leafCount = countLeafDescendants(node);

                // Find and update the row in processedData
                const rowIndex = processedData.findIndex(r => r.id === row.id);
                if (rowIndex !== -1) {
                    processedData[rowIndex] = {
                        ...processedData[rowIndex],
                        LastActivity: latestActivity?.monthDay || row.LastActivity || 'No recent activity',
                        _lastActivityISO: latestActivity?.isoDate, // Store ISO for sorting
                        _lastActivityObj: latestActivity?.dateObj, // Store date object
                        Collected: collectedSum,
                        InvestmentCapital: capitalSum,
                        _leafCount: leafCount,
                        _isParent: true,
                        _childCount: node.children.length
                    };
                }
            } else if (node) {
                // This is a leaf node

                const rowIndex = processedData.findIndex(r => r.id === row.id);

                const collectedSum = computeFractionalProgress(node, 'Collected');

                if (rowIndex !== -1) {
                    const rowIndex = processedData.findIndex(r => r.id === row.id);

                    // Format leaf node's date
                    let formattedDate = row.LastActivity;
                    let isoDate = null;

                    if (row.LastActivity && row.LastActivity !== 0 && typeof row.LastActivity === 'string') {
                        try {
                            const dateObj = new Date(row.LastActivity);
                            if (!isNaN(dateObj.getTime())) {
                                const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
                                    'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
                                formattedDate = `${monthNames[dateObj.getMonth()]} ${dateObj.getDate()}`;
                                isoDate = dateObj.toISOString().split('T')[0];
                            }
                        } catch (e) {
                            // Keep original format if parsing fails
                        }
                    }

                    if (rowIndex !== -1) {
                        processedData[rowIndex] = {
                            ...processedData[rowIndex],
                            LastActivity: formattedDate,
                            _lastActivityISO: isoDate,
                            _lastActivityObj: row.LastActivity ? new Date(row.LastActivity) : null,
                            Collected: collectedSum,
                            _leafCount: 0,
                            _isParent: false,
                            _childCount: 0,
                            _hasLatestActivity: !!row.LastActivity && row.LastActivity !== 0,
                            [GRID_TREE_DATA_GROUPING_FIELD]: '',
                        };
                    }
                }
            }
        });

        return processedData;
    };

    // Helper function to count leaf descendants
    const countLeafDescendants = (node) => {
        let count = 0;
        const stack = [node];

        while (stack.length > 0) {
            const current = stack.pop();
            if (current.children.length === 0) {
                count++;
            } else {
                stack.push(...current.children);
            }
        }

        return count;
    };

    // Compute aggregates once
    const processedData = useMemo(() => computeAggregates(mockTreeData), [mockTreeData]);


    const columns = [
        {
            field: 'Partner',
            headerName: 'Partner',
            flex: 1.2,
            hide: true

        },
        {
            field: 'Company',
            headerName: 'Company',
            flex: 1,
            renderCell: (params) => {
                const isTreeGroup = params.rowNode?.type === 'group';
                const isParent = params.row._isParent;
                const childCount = isTreeGroup
                    ? rowsUnderTreeGroupingKey(params.rowNode.groupingKey, gradeReports).length
                    : (params.row._childCount || 0);
                const treeDepth = params.rowNode?.depth || 0;

                return (
                    <Box sx={{
                        display: 'flex',
                        alignItems: 'center',
                        pl: treeDepth * 4,
                        width: '100%',
                        marginTop: '15px'
                    }}>


                        {(isParent || isTreeGroup) && (
                            <Chip
                                label={`${childCount} Companies`}
                                size="small"
                                sx={{
                                    ml: 1,
                                    height: 20,
                                    fontSize: '0.7rem',
                                    backgroundColor: 'white',
                                    color: 'primary'
                                }}
                            />
                        )}

                        {!isParent && !isTreeGroup && (
                            <Typography variant='body2'>
                                {params.value}
                            </Typography>
                        )}


                    </Box>
                );
            }
        },
        {
            field: 'Status',
            headerName: 'Status',
            flex: 1,
            renderCell: (params) => {
                const isTreeGroup = params.rowNode?.type === 'group';
                const isParent = params.row._isParent;

                const statusColors = {
                    'Pending': '#F7E3BA',
                    'Pending Completion': '#ECDFF2',
                    'Complete': '#E7EBC7',
                    'Completed': '#E7EBC7',
                    'Submitted': '#E7EBC7',
                    'In Progress': '#F7E3BA',
                    'New Request': '#ECDFF2',
                    '60% Progress': '#F7E3BA',
                    'Overdue': '#F7D5D2',
                    'Draft': '#E0E3E5',
                    'Scheduled': '#E0E3E5',
                };

                if (isTreeGroup) {
                    const leaves = rowsUnderTreeGroupingKey(params.rowNode.groupingKey, gradeReports);
                    const text = aggregateLabels(leaves, (r) => r.Status);
                    const singleToneKey = text.includes('·') ? null : text;
                    return (
                        <Box
                            sx={{
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                                fontSize: '0.75rem',
                                fontWeight: 600,
                            }}
                        >
                            <Chip
                                label={text}
                                size="small"
                                sx={{
                                    ml: 1,
                                    height: 20,
                                    fontSize: '0.7rem',
                                    backgroundColor: statusColors[singleToneKey] || '#f5f5f5',
                                    color: 'primary',
                                }}
                            />
                        </Box>
                    );
                }

                if (isParent) {
                    return (
                        <Box
                            sx={{
                                px: 1,
                                py: 0.5,
                                borderRadius: 1,
                                fontSize: '0.75rem',
                                fontWeight: 600,
                            }}
                        />
                    );
                }

                const label = params.value ?? '—';
                return (
                    <Box
                        sx={{
                            px: 1,
                            py: 0.5,
                            borderRadius: 1,
                            fontSize: '0.75rem',
                            fontWeight: 600,
                        }}
                    >
                        <Chip
                            label={label}
                            size="small"
                            sx={{
                                ml: 1,
                                height: 20,
                                fontSize: '0.7rem',
                                backgroundColor: `${statusColors[label] || '#f5f5f5'}`,
                                color: 'primary',
                            }}
                        />
                    </Box>
                );
            }
        },
        {
            field: 'LastActivity',
            headerName: 'Last Activity',
            flex: 1,
            renderCell: (params) => {
                const isTreeGroup = params.rowNode?.type === 'group';
                const isParent = params.row._isParent;

                const formatCell = (v) =>
                    formatLastActivityFromIso(v) ??
                    (v != null && v !== '' && v !== 0 ? String(v) : '—');

                if (isTreeGroup) {
                    const leaves = rowsUnderTreeGroupingKey(params.rowNode.groupingKey, gradeReports);
                    const text = aggregateLabels(leaves, (r) => formatCell(r.LastActivity));
                    return (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            width: '100%',
                            marginTop: '15px'
                        }}>
                            <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                <Chip
                                    label={text}
                                    size="small"
                                    sx={{
                                        ml: 1,
                                        height: 20,
                                        fontSize: '0.7rem',
                                        backgroundColor: 'white',
                                        borderBottom: '1px solid #5A60664D',
                                        color: 'primary'
                                    }}
                                />
                            </Box>
                        </Box>
                    );
                }

                return (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        marginTop: '15px'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>

                            {isParent && (
                                <Chip
                                    label={`${params.value}`}
                                    size="small"
                                    sx={{
                                        ml: 1,
                                        height: 20,
                                        fontSize: '0.7rem',
                                        backgroundColor: 'white',
                                        borderBottom: '1px solid #5A60664D',
                                        color: 'primary'
                                    }}
                                />
                            )}

                            {!isParent && (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 400,
                                        color: '#1A1919'

                                    }}
                                >
                                    {formatCell(params.value)}
                                </Typography>
                            )}

                        </Box>

                    </Box>
                );
            },
        },
        {
            field: 'Collected',
            headerName: 'Collected ($)',
            flex: 1,
            valueFormatter: (params) => `${(params.value || 0).toLocaleString()}`,
            renderCell: (params) => {
                //console.log('Rendering Collected Cell:', params);
                const isParent = params.row._isParent;
                const leafCount = params.row._leafCount || 0;
                const progressValue = params.value.split('/')[0] ? Math.min((params.value.split('/')[0] / 10) * 100, 100) : 0;
                // If it's a string and contains "/10", extract just the numerator

                //console.log('Progress Value:', progressValue);
                return (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        marginTop: '15px'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {isParent && (
                                <Chip
                                    label={`${params.value}`}
                                    size="small"
                                    sx={{
                                        ml: 1,
                                        height: 20,
                                        fontSize: '0.7rem',
                                        backgroundColor: 'white',
                                        borderBottom: '1px solid #5A60664D',
                                        color: 'primary'
                                    }}
                                />
                            )}

                            {!isParent && (
                                <>

                                    {/* Progress Bar */}
                                    <Box sx={{ width: '100%', marginRight: 2 }}>
                                        <LinearProgress
                                            variant="determinate"
                                            value={progressValue}
                                            sx={{
                                                height: 8,
                                                borderRadius: 4,
                                                backgroundColor: 'grey.200',
                                                '& .MuiLinearProgress-bar': {
                                                    borderRadius: 4,
                                                    backgroundColor: progressValue < 50 ? 'warning.main' :
                                                        progressValue < 80 ? 'info.main' :
                                                            'primary.main'
                                                }
                                            }}
                                        />
                                    </Box>

                                    <Typography
                                        variant="body2"
                                        sx={{
                                            fontWeight: 400,
                                            color: 'inherit'
                                        }}
                                    >
                                        {(params.value || 0).toLocaleString()}
                                    </Typography>


                                </>





                            )}

                        </Box>
                    </Box>
                );
            },

        },
        {
            field: 'location',
            headerName: 'Location',
            flex: 1
        },
        {
            field: 'Grade',
            headerName: 'Grade',
            flex: 1,
            renderCell: (params) => {
                return (
                    <Box
                        sx={{
                            textAlign: 'left',
                        }}
                    >
                        {params.value}
                    </Box>
                );
            }
        },
        {
            field: 'InvestmentCapital',
            headerName: 'Inv. Cap',
            flex: 1,
            valueFormatter: (params) => {
                const value = params?.value || 0;

                // Format to millions with one decimal place
                if (value >= 1000000) {
                    return `$${(value / 1000000).toFixed(1)}M`;
                }
                if (value >= 1000) {
                    return `$${(value / 1000).toFixed(0)}K`;
                }
                //  console.log('Formatted Investment Capital:', value);
                return `$${value}`;
            },
            renderCell: (params) => {
                const isParent = params.row._isParent;
                //console.log('isParent:', isParent);
                // Format for display (same as valueFormatter)
                const formatToMillions = (num) => {
                    if (num === undefined || num === null) return '';
                    if (num >= 1000000) {
                        return `$${(num / 1000000).toFixed(1)}M`;
                    }
                    if (num >= 1000) {
                        return `$${(num / 1000).toFixed(0)}K`;
                    }
                    return `$${num}`;
                };

                return (
                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                        marginTop: '15px'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            {isParent && (
                                <Chip
                                    label={`${formatToMillions(params.value)}`}
                                    size="small"
                                    sx={{
                                        ml: 1,
                                        height: 20,
                                        fontSize: '0.7rem',
                                        backgroundColor: 'white',
                                        borderBottom: '1px solid #5A60664D',
                                        color: 'primary'
                                    }}
                                />

                            )}

                            {!isParent && (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        fontWeight: 400,
                                        color: 'inherit'
                                    }}
                                >
                                    {(formatToMillions(params.value) || '').toLocaleString()}
                                </Typography>
                            )}

                        </Box>
                    </Box>
                );
            }
        },
        {
            field: 'CashOutDate',
            headerName: 'Cash Out Date',
            flex: 1
        },

        {
            field: 'actions',
            headerName: '',
            flex: 1,
            sortable: false,
            filterable: false,
            fixed: true,
            align: 'right',
            renderCell: (params) => {

                if (params.row._isParent) {
                    return null; // Return nothing for parent nodes
                }

                // Use params to avoid unused variable warning
                return (
                    <>
                        {isDoneAction ?
                            <CommonButton
                                variant="contained"
                                sx={flagButtonStyles}
                                size="medium"
                                color="secondary"
                                aria-label={`Actions for ${params.row?.name || ''}`}
                            >
                                <ListItemIcon sx={{ minWidth: '0px' }}>
                                    <DoneIcon fontSize="small" />
                                </ListItemIcon>
                            </CommonButton>
                            :
                            <></>

                        }

                        <CommonButton
                            variant="contained"
                            size="medium"
                            color="secondary"
                            sx={{ Height: "34px", width: "40px", padding: 1, marginLeft: 3 }}
                            onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/grade-reports/${requestId}`, {
                                    state: {
                                        requestId: requestId,
                                        statusType: "Submitted",
                                        reportName: location.state?.reportName || location.state?.settingsData?.report_name,  // ← from current page state
                                        reviewMode: true,
                                        settingsData: location.state?.settingsData,
                                    }
                                });
                            }}
                        >
                            Review
                        </CommonButton>

                    </>
                );

            }
        }
    ];

    // Create initial state that hides the tree grouping column
    const initialState = {
        columns: {
            columnVisibilityModel: {
                // [GRID_TREE_DATA_GROUPING_FIELD]: false, // Hide the grouping column
                Partner: false,
                Status: isStatusColumn ? true : false,
                LastActivity: isLastActivityColumn ? true : false,
                Collected: false,
                Grade: isGradeColumn ? true : false,
                InvestmentCapital: isInvestmentCapitalColumn ? true : false,
                CashOutDate: isCashOutDateColumn ? true : false,
                location: isLocationColumn ? true : false,
                actions: isActionColumn ? true : false
            },
        },
    };

    const filteredReports = useMemo(() => {
        if (!gradeReports || gradeReports.length === 0) return [];
        if (!searchText) return gradeReports;

        const query = searchText.toLowerCase();
        return gradeReports.filter((row) =>
            Object.values(row).some((value) =>
                String(value).toLowerCase().includes(query)
            )
        );
    }, [gradeReports, searchText]);


    useImperativeHandle(ref, () => ({
        openColumnsPanel: () => {
            apiRef.current?.showPreferences("columns");
        },
        openFiltersPanel: () => {

            apiRef.current?.showPreferences("filters")

        },
        setReportGridFilter: (params) => {
            console.log('Setting status filter from parent:', params);
            if (params === 'all') {
                apiRef.current?.setFilterModel({ items: [] })
                return;
            }
            if (params === 'pending') {
                apiRef.current?.setFilterModel({
                    items: [{
                        field: 'status', operator: 'isAnyOf', value: ['Pending', 'Pending Completion']
                    }]
                })
                return;
            }
            if (params === 'complete') {
                apiRef.current?.setFilterModel({
                    items: [{
                        field: 'status', operator: 'is', value: 'Complete'
                    }]
                })
                return;
            }
            if (params === 'progress') {
                apiRef.current?.setFilterModel({
                    items: [{
                        field: 'status', operator: 'is', value: '60% Progress'
                    }]
                })
                return;
            }
            if (params === 'pending_completion') {
                apiRef.current?.setFilterModel({
                    items: [{
                        field: 'status', operator: 'is', value: 'Pending Completion'
                    }]
                })
                return;
            }




            apiRef.current?.setFilterModel({
                items: [{
                    field: 'status', operator: 'isAnyOf', value: ['Draft', 'Scheduled', 'Pending Completion', 'Overdue', '60% Progress']
                }]
            })
        },
        clearStatusFilter: () => {
            console.log('Clearing status filter from parent');
            apiRef.current?.setFilterModel({ items: [] })
        }
    }))


    // Function to extract tree path from each row
    const getTreeDataPath = (row) => row.path;

    // Calculate totals for the entire dataset
    // const totalCollected = useMemo(() => {
    //     return processedData
    //         .filter(row => !row._isParent) // Only leaf nodes
    //         .reduce((sum, row) => sum + (row.Collected || 0), 0);
    // }, [processedData]);

    const totalCapital = useMemo(() => {
        return processedData
            .filter(row => !row._isParent) // Only leaf nodes
            .reduce((sum, row) => sum + (row.InvestmentCapital || 0), 0);
    }, [processedData]);

   const handleSelectionModelChange = (newSelectionModel) => {
        setSelectedRows(newSelectionModel);

        if (typeof onCompaniesChange !== 'function') {
            return;
        }

        const selectedCount = newSelectionModel.ids;
        const rows = [];

        [...selectedCount].forEach((id) => {
            const row = apiRef.current?.getRow(id);
            if (row) rows.push(row);
        });

        onCompaniesChange(rows);
    };

    return (
        <Box sx={{ ...(isCheckboxColumn ? gridStyles_2 : gridStyles_1) }}>
            <DataGridPro
                //ref={tableRef}
                apiRef={apiRef}
                rows={filteredReports}
                columns={columns}
                checkboxSelection={Boolean(isCheckboxColumn)}
                onRowSelectionModelChange={
                    isCheckboxColumn || typeof onCompaniesChange === 'function'
                        ? handleSelectionModelChange
                        : undefined
                }
                treeData
                // loading={gradeReports.length === 0}
                getTreeDataPath={getTreeDataPath}
                autoHeight={false}
                defaultGroupingExpansionDepth={-2} // Auto-expand first level
                initialState={initialState}
                pageSizeOptions={[10, 15, 25]}
                groupingColDef={{
                    headerName: 'Partner'
                }}
                getRowClassName={(params) =>
                    params?.row?.path?.length > 1 ? 'child-row' : 'parent-row'
                }
                sx={{
                    '& .MuiDataGrid-cell': {
                        borderBottom: '1px solid #e0e0e0',
                    },

                }}
                hideFooterSelectedRowCount={true}
                hideFooter={true}
                loading={loading}                    // ← use the actual loading state
                slots={{
                    noRowsOverlay: () => (
                        loading ? null : (            // ← show nothing while loading
                            <Box sx={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                height: '100%'
                            }}>
                                <Typography variant="body2" color="text.secondary">
                                    No companies found
                                </Typography>
                            </Box>
                        )
                    ),
                }}
            />

        </Box>
    )
});

export default GradeReports